/**
 * EditBookingModal Component
 *
 * A modal for editing a single booking's project/customer, hours, and absence status.
 * Used from the SelectedBookingSidebar when clicking the edit button on a booking.
 *
 * Supports both:
 * - Hard bookings (project-based)
 * - Soft bookings (customer-based)
 *
 * Ported from Vue EditForm.vue (simplified for single booking edit)
 *
 * @example
 * ```tsx
 * <EditBookingModal
 *   isOpen={isEditModalOpen}
 *   booking={editingBooking}
 *   date={bookingDate}
 *   projects={projects}
 *   customers={customers}
 *   onSave={handleSave}
 *   onClose={() => setIsEditModalOpen(false)}
 * />
 * ```
 */

import { ActionButton } from "@/components/ui/buttons/ActionButton";
import { Checkbox } from "@/components/ui/forms/Checkbox";
import { Icon } from "@/components/ui/display/Icon";
import { Slider } from "@/components/ui/forms/Slider";
import { Modal } from "@/components/ui/feedback/Modal";
import { Select } from "@/components/ui/forms/Select";
import { ProjectCard } from "@/components/ui/display/ProjectCard";
import { Tooltip } from "@/components/ui/feedback/Tooltip";
import type { SelectOption } from "@/components/ui/forms/Select/types";
import type {
  IServiceProject,
  IServiceCustomer,
  IBooking,
} from "@/services/types";
import { BookingType } from "@/services/types";
import { formatWeekdayDate } from "@/utils/date";
import { generateId } from "@/lib/utils";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./EditBookingModal.module.css";
import type { BookingUpdate, EditBookingModalProps } from "./types";

// Fallback working day hours (when hoursInDay prop not provided)
const FALLBACK_DAY_HOURS = 8;
// Maximum hours when overtime is allowed
const MAX_OVERTIME_HOURS = 16;

/**
 * Check if a booking is an absence booking
 * Uses b_type field as the source of truth (ABSENCE = 0)
 */
function isAbsenceBooking(booking: IBooking): boolean {
  // Primary check: booking type is ABSENCE (0)
  if (booking.b_type === BookingType.ABSENCE) return true;
  // Fallback: project name is "Fravær" (legacy check)
  return booking.project?.name === "Fravær";
}

/**
 * Check if a booking is a soft booking
 * Uses b_type field as the source of truth (SOFT_BOOKING = 2)
 */
function isSoftBookingType(booking: IBooking): boolean {
  return booking.b_type === BookingType.SOFT_BOOKING;
}

/**
 * Convert project to select option
 */
function projectToOption(
  project: IServiceProject,
): SelectOption<IServiceProject> {
  const label =
    project.name === "Fravær"
      ? project.name
      : `${project.code} - ${project.name}`;
  return {
    id: project.id,
    label,
    item: project,
  };
}

/**
 * Convert customer to select option
 */
function customerToOption(
  customer: IServiceCustomer,
): SelectOption<IServiceCustomer> {
  return {
    id: customer.id,
    label: customer.name,
    data: customer,
  };
}

export const EditBookingModal: React.FC<EditBookingModalProps> = ({
  isOpen,
  booking,
  date,
  projects = [],
  customers = [],
  hoursInDay = FALLBACK_DAY_HOURS,
  isCustomerTarget,
  onSave,
  onClose,
  "data-testid": testId,
}) => {
  // Determine if this is a soft booking (detected from the booking itself)
  const isSoftBooking = useMemo(
    () => (booking ? isSoftBookingType(booking) : false),
    [booking],
  );

  // Form state
  const [selectedProject, setSelectedProject] =
    useState<IServiceProject | null>(null);
  const [selectedCustomer, setSelectedCustomer] =
    useState<IServiceCustomer | null>(null);
  const [isAbsence, setIsAbsence] = useState(false);
  const [hours, setHours] = useState(hoursInDay);
  const [allowOvertime, setAllowOvertime] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Local string state for the hours input to prevent type="number" desync (Bug #9098)
  const [hoursInputValue, setHoursInputValue] = useState(String(hours));
  const isCustomerOnlySoftBooking = isSoftBooking && !!isCustomerTarget;

  // Bug #2 fix: Preserve project selection before toggling absence ON
  const [preAbsenceProject, setPreAbsenceProject] =
    useState<IServiceProject | null>(null);

  // Initialize form state when booking changes
  useEffect(() => {
    if (booking) {
      const isAbsence = isAbsenceBooking(booking);
      const isSoft = isSoftBookingType(booking);

      if (isSoft) {
        // Soft booking - set customer
        setSelectedCustomer(booking.customer ?? null);
        setSelectedProject(null);
      } else {
        // Hard booking - set project
        setSelectedProject(isAbsence ? null : (booking.project ?? null));
        setSelectedCustomer(null);
      }

      setIsAbsence(isAbsence);
      setHours(booking.hours);
      setAllowOvertime(
        isCustomerOnlySoftBooking ? false : booking.hours > hoursInDay,
      );
      setError(null);
      setPreAbsenceProject(null); // Reset saved project on booking change
    }
  }, [booking]);

  // Filter out absence project from options (handled by checkbox)
  const projectOptions = useMemo(() => {
    return projects.filter((p) => p.name !== "Fravær").map(projectToOption);
  }, [projects]);

  // Customer options
  const customerOptions = useMemo(() => {
    return customers.map(customerToOption);
  }, [customers]);

  // Current project selection for Select component
  const selectedProjectOption = useMemo((): SelectOption<IServiceProject>[] => {
    if (!selectedProject || isAbsence) return [];
    return [projectToOption(selectedProject)];
  }, [selectedProject, isAbsence]);

  // Current customer selection for Select component
  const selectedCustomerOption =
    useMemo((): SelectOption<IServiceCustomer>[] => {
      if (!selectedCustomer) return [];
      return [customerToOption(selectedCustomer)];
    }, [selectedCustomer]);

  // Sync local input display when hours changes externally (slider, overtime toggle, init)
  useEffect(() => {
    setHoursInputValue(String(hours));
  }, [hours]);

  // Max hours based on overtime toggle
  const maxHours = useMemo<number | undefined>(() => {
    if (isCustomerOnlySoftBooking) return undefined;
    return allowOvertime ? MAX_OVERTIME_HOURS : hoursInDay;
  }, [isCustomerOnlySoftBooking, allowOvertime, hoursInDay]);

  // Modal title - matches Vue format "Rediger booking for den mandag 6. januar"
  const modalTitle = `Rediger booking for ${formatWeekdayDate(date)}`;

  // Edit hint text - shows project or customer name depending on booking type
  const editHint = useMemo(() => {
    if (!booking) return "";

    if (isSoftBooking) {
      // Soft booking - show customer name
      const customerName = booking.customer?.name ?? "ukjent kunde";
      return `Redigerer 1 booking på '${customerName}'`;
    }

    // Hard booking - show project name
    const getProjectName = () => {
      if (booking.project?.name === "Fravær") return "Fravær";
      if (booking.project)
        return `${booking.project.code} - ${booking.project.name}`;
      return "ukjent prosjekt";
    };
    return `Redigerer 1 booking på '${getProjectName()}'`;
  }, [booking, isSoftBooking]);

  // Handle project selection
  const handleProjectChange = useCallback(
    (options: SelectOption<IServiceProject>[]) => {
      if (options.length > 0 && options[0].item) {
        setSelectedProject(options[0].item);
        setError(null);
      } else {
        setSelectedProject(null);
      }
    },
    [],
  );

  // Handle customer selection
  const handleCustomerChange = useCallback(
    (options: SelectOption<IServiceCustomer>[]) => {
      if (options.length > 0 && options[0].data) {
        setSelectedCustomer(options[0].data);
        setError(null);
      } else {
        setSelectedCustomer(null);
      }
    },
    [],
  );

  // Handle absence toggle (only for hard bookings)
  const handleAbsenceToggle = useCallback(() => {
    setIsAbsence((prev) => {
      const togglingOn = !prev;

      if (togglingOn) {
        // Toggling ON: Save current project selection before clearing it
        setPreAbsenceProject(selectedProject);
        setSelectedProject(null);
      } else {
        // Toggling OFF: Restore saved project (or original booking project as fallback)
        if (preAbsenceProject) {
          setSelectedProject(preAbsenceProject);
        } else if (booking?.project && booking.project.name !== "Fravær") {
          setSelectedProject(booking.project);
        }
        setPreAbsenceProject(null);
      }

      return togglingOn;
    });
    setError(null);
  }, [selectedProject, preAbsenceProject, booking]);

  // Handle overtime toggle
  const handleOvertimeToggle = useCallback(() => {
    if (isCustomerOnlySoftBooking) return;

    setAllowOvertime((prev) => {
      const newValue = !prev;
      if (!newValue && hours > hoursInDay) {
        setHours(hoursInDay);
      }
      return newValue;
    });
  }, [isCustomerOnlySoftBooking, hours, hoursInDay]);

  // Handle hours change from slider
  const handleHoursChange = useCallback((value: number) => {
    setHours(value);
  }, []);

  // Handle hours input change - update local display freely, clamp on blur
  const handleHoursInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setHoursInputValue(e.target.value);
    },
    [],
  );

  // Validate and clamp hours on blur
  const handleHoursInputBlur = useCallback(() => {
    const parsed = parseFloat(hoursInputValue.replace(",", "."));
    if (!isNaN(parsed) && parsed > 0) {
      const clamped =
        maxHours === undefined
          ? Math.max(0.5, parsed)
          : Math.max(0.5, Math.min(maxHours, parsed));
      setHours(clamped);
      setHoursInputValue(String(clamped));
    } else {
      // Reset to current valid hours on invalid input
      setHoursInputValue(String(hours));
    }
  }, [hoursInputValue, maxHours, hours]);

  // Validate form
  const isValid = useMemo(() => {
    if (hours <= 0) return false;

    if (isSoftBooking) {
      // Soft booking needs a customer selected
      return !!selectedCustomer;
    }

    // Hard booking needs either absence or a project
    if (!isAbsence && !selectedProject) return false;
    return true;
  }, [hours, isSoftBooking, selectedCustomer, isAbsence, selectedProject]);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!isValid) return;

    setIsSaving(true);
    setError(null);

    try {
      const update: BookingUpdate = {
        // Project fields (for hard bookings)
        projectId:
          isSoftBooking || isAbsence ? null : (selectedProject?.id ?? null),
        project: isSoftBooking || isAbsence ? null : selectedProject,
        // Customer fields (for soft bookings)
        customerId: isSoftBooking ? (selectedCustomer?.id ?? null) : null,
        customer: isSoftBooking ? selectedCustomer : null,
        hours,
        isAbsence,
        isSoftBooking,
      };

      await onSave(update);
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Kunne ikke lagre endringer",
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    isValid,
    isSoftBooking,
    isAbsence,
    selectedProject,
    selectedCustomer,
    hours,
    onSave,
    onClose,
  ]);

  return (
    <Modal
      isOpen={isOpen}
      title={modalTitle}
      onClose={onClose}
      closeOnOverlayClick={false}
      size="xl"
      data-testid={testId}
    >
      <div className={styles.container}>
        {/* Edit hint */}
        <p className={styles.editHint}>{editHint}</p>

        {/* Soft booking: Customer selector */}
        {isSoftBooking && (
          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>Ny kunde</legend>
            <Select<IServiceCustomer>
              options={customerOptions}
              value={selectedCustomerOption}
              onChange={handleCustomerChange}
              placeholder="Velg kunde..."
              searchable
              disabled={isSaving}
              data-testid={
                testId ? `${testId}-customer` : "booking-edit-customer"
              }
            />
          </fieldset>
        )}

        {/* Hard booking: Project selector (hidden when absence is checked) */}
        {!isSoftBooking && !isAbsence && (
          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>Nytt prosjekt</legend>
            <Select<IServiceProject>
              options={projectOptions}
              value={selectedProjectOption}
              onChange={handleProjectChange}
              placeholder="Velg prosjekt..."
              searchable
              disabled={isSaving}
              renderOption={(option, state) => {
                const project = option.item ?? option.data;
                return (
                  <ProjectCard
                    code={project?.code ?? String(option.id)}
                    name={project?.name ?? option.label}
                    variant="default"
                    selected={state.selected}
                    disabled={state.disabled}
                  />
                );
              }}
              data-testid={
                testId ? `${testId}-project` : "booking-edit-project"
              }
            />
          </fieldset>
        )}

        {/* Hours fieldset - groups checkboxes, slider, input, and submit */}
        <fieldset className={styles.hoursFieldset}>
          <legend className={styles.legend}>Timeregistrering</legend>

          {/* Checkboxes row - Absence (hard booking only) and Overtime */}
          <div className={styles.checkboxRow}>
            {/* Absence checkbox - only for hard bookings */}
            {!isSoftBooking && (
              <Tooltip
                content="Registrer fravær i stedet for prosjekttimer. Prosjektvalg fjernes når dette er aktivert."
                position="top"
                width="sm"
              >
                <div className={styles.checkboxWrapper}>
                  <Checkbox
                    id={generateId("booking-edit-absence")}
                    checked={isAbsence}
                    onChange={handleAbsenceToggle}
                    label="Fravær"
                    disabled={isSaving}
                    data-testid={
                      testId ? `${testId}-absence` : "booking-edit-absence"
                    }
                  />
                </div>
              </Tooltip>
            )}

            {!(isCustomerOnlySoftBooking || isAbsence) && (
              <Tooltip
                content={`Tillat registrering av mer enn ${hoursInDay} timer per dag (opptil 16 timer).`}
                position="top"
                width="sm"
              >
                <div className={styles.checkboxWrapper}>
                  <Checkbox
                    id={generateId("booking-edit-overtime")}
                    checked={allowOvertime}
                    onChange={handleOvertimeToggle}
                    label={`Over ${hoursInDay} timer`}
                    disabled={isSaving}
                    data-testid={
                      testId ? `${testId}-overtime` : "booking-edit-overtime"
                    }
                  />
                </div>
              </Tooltip>
            )}
          </div>

          {isCustomerOnlySoftBooking ? (
            <div className={styles.hoursRow}>
              <input
                type="text"
                inputMode="decimal"
                value={hoursInputValue}
                onChange={handleHoursInputChange}
                onBlur={handleHoursInputBlur}
                className={styles.hoursInput}
                disabled={isSaving}
                data-testid={testId ? `${testId}-hours` : "booking-edit-hours"}
                aria-label="Timer"
              />
            </div>
          ) : (
            <div className={styles.hoursRow}>
              <div className={styles.sliderContainer}>
                <Slider
                  value={hours}
                  onChange={handleHoursChange}
                  min={0.5}
                  max={maxHours!}
                  step={0.5}
                  label="Timer"
                  disabled={isSaving}
                  data-testid={
                    testId ? `${testId}-slider` : "booking-edit-slider"
                  }
                />
              </div>

              <input
                type="text"
                inputMode="decimal"
                value={hoursInputValue}
                onChange={handleHoursInputChange}
                onBlur={handleHoursInputBlur}
                className={styles.hoursInput}
                disabled={isSaving}
                data-testid={testId ? `${testId}-hours` : "booking-edit-hours"}
                aria-label="Timer"
              />
            </div>
          )}

          {/* Error message */}
          {error && <div className={styles.error}>{error}</div>}

          {/* Submit button inside fieldset */}
          <div className={styles.actions}>
            <ActionButton
              label="Lagre endringer"
              variant="primary"
              icon={<Icon name="save" size="sm" aria-hidden />}
              iconPosition="right"
              onClick={handleSave}
              disabled={!isValid || isSaving}
              data-testid={testId ? `${testId}-submit` : "booking-edit-submit"}
            />
          </div>
        </fieldset>
      </div>
    </Modal>
  );
};

EditBookingModal.displayName = "EditBookingModal";

export default EditBookingModal;
